import Auth from "@/modules/Auth";
import NewPasswordPage from "@/modules/Auth/pages/NewPasswordPage";
import ProjectsMain from "@/modules/Projects";
import { FC } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router";
import { useSelector } from "react-redux";
import Clients from "@/modules/Clients";
import UserManagement from "@/modules/UserManagement";
import TimeEntryMain from "@/modules/TimeEntry";
import AllTimeEntries from "@/modules/AllTimeEntries";
import ProjectTrackingReport from "@/modules/ProjectTrackingReport";
import ClientProject from "@/modules/Projects/clientProject";
import {
  selectIsAuthenticated,
  selectUserRole,
} from "@/store/slices/authSlice";
import { hasMenuPermission, ROLES } from "@/utils/constants";
import MonthBreakdown from "@/modules/MonthBreakdown";
import PrivacyPolicy from "@/modules/PrivacyPolicy&TermsConditions/PrivacyPolicy";
import TermsOfService from "@/modules/PrivacyPolicy&TermsConditions/TermsOfService";
import QuickbookConnection from "@/modules/QuickbookConnection";
import Email from "@/modules/Email";
import EmailTemplatePage from "@/modules/EmailTemplate";

// Protected route component
const ProtectedRoute: FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const location = useLocation();

  if (!isAuthenticated) {
    // Redirect to login page if not authenticated
    return <Navigate to="/auth/signin" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// Public route component (redirects to dashboard if already authenticated)
const PublicRoute: FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  if (isAuthenticated) {
    // Redirect to dashboard if already authenticated
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
};

const AppRoutes: FC = () => {
  const userRoles = useSelector(selectUserRole);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Navigate to="/projects" replace />} />
      <Route path="/auth">
        <Route
          path="signin"
          element={
            <PublicRoute>
              <Auth />
            </PublicRoute>
          }
        />
        <Route
          path="generate-password"
          element={
            <PublicRoute>
              <NewPasswordPage />
            </PublicRoute>
          }
        />
      </Route>

      <Route path="/public/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/public/terms-of-service" element={<TermsOfService />} />

      {/* Protected routes */}
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            {hasMenuPermission(userRoles, [ROLES.CLIENT]) ? (
              <ClientProject />
            ) : (
              <ProjectsMain />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/email"
        element={
          <ProtectedRoute>
            <Email />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-templates"
        element={
          <ProtectedRoute>
            <EmailTemplatePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <Clients />
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee-management"
        element={
          <ProtectedRoute>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/time-entry"
        element={
          <ProtectedRoute>
            <TimeEntryMain />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={<Navigate to="/reports/time-entries" replace />}
      />
      <Route
        path="/reports/time-entries"
        element={
          <ProtectedRoute>
            <AllTimeEntries />
          </ProtectedRoute>
        }
      />
      <Route path="/reports/project-tracking">
        <Route
          index
          element={
            <ProtectedRoute>
              <ProjectTrackingReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="month-breakdown/:projectId"
          element={
            <ProtectedRoute>
              <MonthBreakdown />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route
        path="/quickbook-connection"
        element={
          <ProtectedRoute>
            <QuickbookConnection />
          </ProtectedRoute>
        }
      />

      {/* Fallback route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRoutes;
